import { formatText, itemsSource } from "@eusoft/webapp-ui";
import { ColorBox } from "../components/ColorBox";
import { SortDirection, type CommandMap, type Guid, type ICardListView, type IEntityView, type IListCard, type IListMerchant, type IListProduct, type IListResult, type IListSalePoint, type IMerchantListView, type IProductListView, type ISalePointListView } from "../entities/Commands";
import { executeAsync, executeCacheAsync } from "../services/DataService";
import type { IItemsSource } from "@eusoft/webapp-ui";

type ElementType<T> = T extends readonly (infer U)[] ? U : never;

export const PALETTE = [
    { "name": "Red", "hex": "#F44336" },
    { "name": "Pink", "hex": "#E91E63" },
    { "name": "Purple", "hex": "#9C27B0" },
    { "name": "Deep Purple", "hex": "#673AB7" },
    { "name": "Indigo", "hex": "#3F51B5" },
    { "name": "Blue", "hex": "#2196F3" },
    { "name": "Light Blue", "hex": "#03A9F4" },
    { "name": "Cyan", "hex": "#00BCD4" },
    { "name": "Teal", "hex": "#009688" },
    { "name": "Green", "hex": "#4CAF50" },
    { "name": "Light Green", "hex": "#8BC34A" },
    { "name": "Lime", "hex": "#CDDC39" },
    { "name": "Yellow", "hex": "#FFEB3B" },
    { "name": "Amber", "hex": "#FFC107" },
    { "name": "Orange", "hex": "#FF9800" },
    { "name": "Deep Orange", "hex": "#FF5722" },
    { "name": "Brown", "hex": "#795548" },
    { "name": "Gray", "hex": "#9E9E9E" },
    { "name": "Blue Gray", "hex": "#607D8B" }
]

export const colorsSource: IItemsSource<string, string, void> = {

    getItemsAsync: async () => {
        return PALETTE.map(a => a.hex);
    },

    getIcon: a => <ColorBox color={a}/>,
    getValue: a => a,
    getText: a => a,
}

export function entityItemsSource<
    TItem extends IEntityView,
    TCommand extends keyof CommandMap,
    TResult extends IListResult<TItem> & CommandMap[TCommand]["result"]>

    (itemsOrCommand: TCommand | TItem[], useCache = false, defaultFilter?: Partial<CommandMap[TCommand]["command"]>) {

    return itemsSource({
        getText: a => a.name,
        getValue: a => a.id,        
        getItemsAsync: async filter => { 

            filter = {
                ...defaultFilter,
                ...filter
            }

            if (Array.isArray(itemsOrCommand))
                return itemsOrCommand;

            if (useCache)
                return ((await executeCacheAsync(itemsOrCommand, filter)) as TResult).items;
                
            const result = (await executeAsync(itemsOrCommand, filter as any)) as TResult; //TS shit
            return result?.items;
        }
    } as IItemsSource<ElementType<TResult["items"]>, Guid, CommandMap[TCommand]["command"]>)
}

/*Merchant*/

export const merchantItemsSource = {
    ...entityItemsSource("ListMerchant", false, {
        sort: {
            direction: SortDirection.Ascending,
            property: "Name"
        }
    }),
    getItemByValueAsync: async (value: Guid) => {

        return (await executeAsync("ListMerchant", {
            ids: [value]
        }))?.items[0];
    },
    getText: a => a.city ? formatText("item-with-info", a.name, a.city) : a.name,
} as IItemsSource<IMerchantListView, Guid, IListMerchant>


/*Cards*/

export const cardsItemsSource = {
    ...entityItemsSource("ListCard"),

    getItemByValueAsync: async (value: Guid) => {

        return (await executeAsync("ListCard", {
            ids: [value]
        }))?.items[0];
    }
        
} as IItemsSource<ICardListView, Guid, IListCard>

/*Product*/

export const productItemsSource = {
    ...entityItemsSource("ListProduct", false, {
        sort: {
            direction: SortDirection.Ascending,
            property: "Name"
        }
    }),

    getItemByValueAsync: async (value: Guid) => {

        return (await executeAsync("ListProduct", {
            ids: [value]
        }))?.items[0];
    }

} as IItemsSource<IProductListView, Guid, IListProduct>

/*SalePoint*/

export const salePointsItemsSource = {

    ...entityItemsSource("ListSalePoint", false, {
        sort: {
            direction: SortDirection.Ascending,
            property: "Name"
        }
    }),

    getItemByValueAsync: async (value: Guid) => {

        return (await executeAsync("ListSalePoint", {
            ids: [value]
        }))?.items[0];
    }

} as IItemsSource<ISalePointListView, Guid, IListSalePoint>